import { useState, useMemo, useCallback, useEffect, ReactNode } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type VisibilityState,
  type ColumnSizingState,
} from '@tanstack/react-table';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Columns3,
  RotateCcw,
  X,
  Filter
} from 'lucide-react';
import { EmptyTableState, TableSkeleton } from '../EmptyState';
import type { DataTableProps } from './types';

// Densité des cellules
const densityStyles = {
  compact: 'px-2 py-1 text-xs',
  normal: 'px-4 py-3 text-sm',
  comfortable: 'px-6 py-4 text-sm'
};

const densityHeaderStyles = {
  compact: 'px-2 py-2 text-xs',
  normal: 'px-4 py-3 text-xs',
  comfortable: 'px-6 py-4 text-xs'
};

/**
 * DataTable - Composant de table avancé avec fonctionnalités Excel-like
 * Compatible avec l'ancien format de colonnes Table.tsx
 */
export function DataTable<T extends { id: string | number }>({
  columns,
  data,
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  emptyTitle = "Aucune donnée",
  emptyAction,
  onRowClick,
  rowClassName,
  containerClassName,
  tableClassName,
  title,
  showToolbar = true,
  showPagination = true,
  pageSize: defaultPageSize = 20,
  pageSizeOptions = [10, 20, 50, 100],
  enableResize = true,
  enableSort = true,
  enableFilters = false,
  enableColumnToggle = true,
  enableGlobalFilter = true,
  storageKey,
  maxHeight,
  density = 'normal',
  stickyFirstColumn = false,
  showRowNumbers = false,
  toolbarActions,
  // Pagination externe
  totalItems,
  page: externalPage,
  totalPages: externalTotalPages,
  onPageChange,
  onPageSizeChange
}: DataTableProps<T>) {
  // Protéger les données
  const safeData = useMemo(() => 
    Array.isArray(data) ? data.filter(item => item != null && item.id != null) : [],
    [data]
  );
  const safeColumns = useMemo(() => Array.isArray(columns) ? columns : [], [columns]);

  // États
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    // Initialiser avec les colonnes masquées par défaut
    const initial: VisibilityState = {};
    safeColumns.forEach((col, idx) => {
      if (col.hidden) {
        initial[`col_${idx}`] = false;
      }
    });
    return initial;
  });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showFiltersRow, setShowFiltersRow] = useState(false);

  // Pagination interne
  const [internalPage, setInternalPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(defaultPageSize);

  // Utiliser pagination externe si fournie
  const isExternalPagination = externalPage !== undefined && onPageChange !== undefined;
  const currentPage = isExternalPagination ? (externalPage - 1) : internalPage;
  const currentPageSize = internalPageSize;

  // Charger état depuis localStorage
  useEffect(() => {
    if (storageKey) {
      try {
        const saved = localStorage.getItem(`datatable-${storageKey}`);
        if (saved) {
          const state = JSON.parse(saved);
          if (state.columnVisibility) setColumnVisibility(state.columnVisibility);
          if (state.columnSizing) setColumnSizing(state.columnSizing);
          if (state.sorting) setSorting(state.sorting);
          if (state.pageSize) setInternalPageSize(state.pageSize);
        }
      } catch (e) {
        console.warn('Failed to load DataTable state:', e);
      }
    }
  }, [storageKey]);

  // Sauvegarder état
  const saveState = useCallback(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`datatable-${storageKey}`, JSON.stringify({
          columnVisibility,
          columnSizing,
          sorting,
          pageSize: internalPageSize
        }));
      } catch (e) {
        console.warn('Failed to save DataTable state:', e);
      }
    }
  }, [storageKey, columnVisibility, columnSizing, sorting, internalPageSize]);

  useEffect(() => {
    saveState();
  }, [saveState]);

  // Convertir les colonnes au format TanStack
  const tanstackColumns = useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = [];

    // Colonne numéro de ligne
    if (showRowNumbers) {
      cols.push({
        id: '_rowNumber',
        header: '#',
        cell: ({ row }) => (
          <span className="text-muted-foreground font-mono text-xs">
            {row.index + 1 + currentPage * currentPageSize}
          </span>
        ),
        size: 50,
        enableSorting: false,
        enableResizing: false,
      });
    }

    // Colonnes utilisateur
    safeColumns.forEach((col, index) => {
      const colId = `col_${index}`;
      
      cols.push({
        id: colId,
        accessorFn: (row) => {
          if (typeof col.accessor === 'function') {
            return col.accessor(row);
          }
          return row[col.accessor as keyof T];
        },
        header: ({ column }) => {
          if (!enableSort || col.sortable === false) {
            return <span>{col.header}</span>;
          }
          
          const sorted = column.getIsSorted();
          return (
            <button
              className="flex items-center gap-1 hover:text-foreground transition-colors group w-full text-left"
              onClick={() => column.toggleSorting(sorted === 'asc')}
            >
              <span className="flex-1">{col.header}</span>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                {sorted === 'asc' ? (
                  <ArrowUp className="h-3.5 w-3.5" />
                ) : sorted === 'desc' ? (
                  <ArrowDown className="h-3.5 w-3.5" />
                ) : (
                  <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </span>
            </button>
          );
        },
        cell: ({ row }) => {
          const value = typeof col.accessor === 'function'
            ? col.accessor(row.original)
            : row.original[col.accessor as keyof T];
          
          return (
            <div className={col.className}>
              {value as ReactNode}
            </div>
          );
        },
        size: typeof col.width === 'number' ? col.width : undefined,
        minSize: col.minWidth || 80,
        maxSize: col.maxWidth,
        enableSorting: enableSort && col.sortable !== false,
        enableResizing: enableResize,
        enableHiding: col.hideable !== false,
      });
    });

    return cols;
  }, [safeColumns, showRowNumbers, enableSort, enableResize, currentPage, currentPageSize]);

  // Configuration de la table
  const table = useReactTable({
    data: safeData,
    columns: tanstackColumns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnSizing,
      globalFilter,
      pagination: {
        pageIndex: currentPage,
        pageSize: currentPageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' 
        ? updater({ pageIndex: currentPage, pageSize: currentPageSize })
        : updater;
      
      if (isExternalPagination) {
        if (newState.pageIndex !== currentPage) {
          onPageChange(newState.pageIndex + 1);
        }
        if (newState.pageSize !== currentPageSize && onPageSizeChange) {
          onPageSizeChange(newState.pageSize);
        }
      } else {
        setInternalPage(newState.pageIndex);
        setInternalPageSize(newState.pageSize);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: isExternalPagination ? undefined : getPaginationRowModel(),
    manualPagination: isExternalPagination,
    pageCount: externalTotalPages,
    columnResizeMode: 'onChange',
    enableColumnResizing: enableResize,
  });

  // Réinitialiser les paramètres
  const handleReset = () => {
    setSorting([]);
    setColumnFilters([]);
    setGlobalFilter('');
    setColumnVisibility({});
    setShowFiltersRow(false);
    if (storageKey) {
      localStorage.removeItem(`datatable-${storageKey}`);
    }
  };

  // Calculs pagination
  const totalRows = isExternalPagination ? (totalItems || 0) : table.getFilteredRowModel().rows.length;
  const totalPagesCalc = isExternalPagination 
    ? (externalTotalPages || 1) 
    : Math.ceil(totalRows / currentPageSize);
  const startRow = currentPage * currentPageSize + 1;
  const endRow = Math.min((currentPage + 1) * currentPageSize, totalRows);

  return (
    <div className={`flex flex-col gap-3 ${containerClassName || ''}`}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-2 px-1">
          {/* Titre */}
          {title && (
            <h3 className="font-medium text-foreground mr-4">{title}</h3>
          )}
          
          {/* Recherche globale */}
          {enableGlobalFilter && (
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {globalFilter && (
                <button
                  onClick={() => setGlobalFilter('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-1 ml-auto">
            {/* Toggle filtres */}
            {enableFilters && (
              <button
                onClick={() => setShowFiltersRow(!showFiltersRow)}
                className={`p-2 rounded-md transition-colors ${
                  showFiltersRow ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                }`}
                title="Filtres par colonne"
              >
                <Filter className="h-4 w-4" />
              </button>
            )}

            {/* Sélecteur de colonnes */}
            {enableColumnToggle && (
              <div className="relative">
                <button
                  onClick={() => setShowColumnMenu(!showColumnMenu)}
                  className={`p-2 rounded-md transition-colors ${
                    showColumnMenu ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'
                  }`}
                  title="Colonnes visibles"
                >
                  <Columns3 className="h-4 w-4" />
                </button>
                
                {showColumnMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-popover border border-border rounded-lg shadow-lg py-1">
                    <div className="px-3 py-2 border-b border-border">
                      <span className="text-xs font-medium text-muted-foreground uppercase">Colonnes</span>
                    </div>
                    {table.getAllLeafColumns()
                      .filter(col => col.id !== '_rowNumber' && col.getCanHide())
                      .map(column => {
                        const colDef = safeColumns[parseInt(column.id.replace('col_', ''))] ;
                        return (
                          <label
                            key={column.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={column.getIsVisible()}
                              onChange={column.getToggleVisibilityHandler()}
                              className="rounded border-input"
                            />
                            <span className="text-sm">
                              {colDef?.header || column.id}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                )}
              </div>
            )}

            {/* Réinitialiser */}
            <button
              onClick={handleReset}
              className="p-2 rounded-md hover:bg-muted text-muted-foreground transition-colors"
              title="Réinitialiser"
            >
              <RotateCcw className="h-4 w-4" />
            </button>

            {/* Actions personnalisées */}
            {toolbarActions}
          </div>
        </div>
      )}

      {/* Table */}
      <div 
        className={`relative border border-border rounded-lg ${tableClassName || ''}`}
        style={{ maxHeight: maxHeight }}
      >
        <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: maxHeight }}>
          <table 
            className="w-full border-collapse"
            style={{ tableLayout: 'fixed', minWidth: '100%' }}
          >
            <thead className="bg-muted sticky top-0 z-10">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, idx) => (
                    <th
                      key={header.id}
                      className={`
                        ${densityHeaderStyles[density]}
                        text-left font-medium text-muted-foreground uppercase tracking-wider
                        border-b border-r border-border last:border-r-0 relative select-none
                        ${stickyFirstColumn && idx === 0 ? 'sticky left-0 z-20 bg-muted dark:bg-muted' : ''}
                      `}
                      style={{ 
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize || 80,
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                      
                      {/* Resize handle */}
                      {enableResize && header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`
                            absolute right-0 top-0 h-full w-1 cursor-col-resize
                            hover:bg-primary/50 active:bg-primary
                            ${header.column.getIsResizing() ? 'bg-primary' : 'bg-transparent'}
                          `}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
              
              {/* Ligne de filtres */}
              {showFiltersRow && enableFilters && (
                <tr className="bg-muted/50 dark:bg-muted/20">
                  {table.getHeaderGroups()[0].headers.map((header, idx) => {
                    const colDef = safeColumns[parseInt(header.id.replace('col_', ''))];
                    const canFilter = colDef?.filterable !== false;
                    
                    return (
                      <th
                        key={`filter-${header.id}`}
                        className={`
                          px-2 py-2 border-b border-r border-border last:border-r-0
                          ${stickyFirstColumn && idx === 0 ? 'sticky left-0 z-20 bg-muted/50 dark:bg-muted/20' : ''}
                        `}
                      >
                        {canFilter && header.id !== '_rowNumber' ? (
                          <input
                            type="text"
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={e => header.column.setFilterValue(e.target.value)}
                            placeholder="Filtrer..."
                            className="w-full h-7 px-2 text-xs rounded border border-input bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                          />
                        ) : null}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            
            <tbody className="bg-background">
              {loading ? (
                <TableSkeleton rows={5} columns={tanstackColumns.length} />
              ) : table.getRowModel().rows.length === 0 ? (
                <EmptyTableState
                  title={emptyTitle}
                  description={emptyMessage}
                  colSpan={tanstackColumns.length}
                  action={emptyAction}
                />
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row.original)}
                    className={`
                      border-b border-border last:border-b-0
                      transition-colors
                      hover:bg-muted/50 dark:hover:bg-muted/30
                      ${onRowClick ? 'cursor-pointer' : ''}
                      ${rowClassName ? rowClassName(row.original) : ''}
                    `}
                  >
                    {row.getVisibleCells().map((cell, idx) => (
                      <td
                        key={cell.id}
                        className={`
                          ${densityStyles[density]}
                          text-foreground
                          border-r border-border last:border-r-0
                          ${stickyFirstColumn && idx === 0 ? 'sticky left-0 z-10 bg-background dark:bg-background border-r' : ''}
                        `}
                      >
                        <div className={`${cell.column.id.includes('action') || cell.column.id === 'col_' + String(tanstackColumns.length - 1) ? 'overflow-visible whitespace-nowrap' : 'overflow-hidden truncate'}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {showPagination && totalRows > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 px-1">
          {/* Info */}
          <div className="text-sm text-muted-foreground">
            <span className="hidden sm:inline">
              Affichage de{' '}
              <span className="font-medium text-foreground">{startRow}</span>
              {' '}à{' '}
              <span className="font-medium text-foreground">{endRow}</span>
              {' '}sur{' '}
              <span className="font-medium text-foreground">{totalRows}</span>
              {' '}résultats
            </span>
            <span className="sm:hidden text-xs">
              {startRow}-{endRow} / {totalRows}
            </span>
          </div>

          {/* Contrôles */}
          <div className="flex items-center gap-2">
            {/* Sélecteur taille page */}
            <select
              value={currentPageSize}
              onChange={e => {
                const newSize = Number(e.target.value);
                if (isExternalPagination && onPageSizeChange) {
                  onPageSizeChange(newSize);
                } else {
                  setInternalPageSize(newSize);
                  setInternalPage(0);
                }
              }}
              className="h-8 px-2 text-sm rounded-md border border-input bg-background"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>

            {/* Navigation */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  if (isExternalPagination) {
                    onPageChange(1);
                  } else {
                    setInternalPage(0);
                  }
                }}
                disabled={currentPage === 0}
                className="p-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Première page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  if (isExternalPagination) {
                    onPageChange(currentPage);
                  } else {
                    setInternalPage(p => Math.max(0, p - 1));
                  }
                }}
                disabled={currentPage === 0}
                className="p-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <span className="px-3 text-sm">
                Page <span className="font-medium">{currentPage + 1}</span> sur{' '}
                <span className="font-medium">{totalPagesCalc}</span>
              </span>

              <button
                onClick={() => {
                  if (isExternalPagination) {
                    onPageChange(currentPage + 2);
                  } else {
                    setInternalPage(p => Math.min(totalPagesCalc - 1, p + 1));
                  }
                }}
                disabled={currentPage >= totalPagesCalc - 1}
                className="p-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Page suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  if (isExternalPagination) {
                    onPageChange(totalPagesCalc);
                  } else {
                    setInternalPage(totalPagesCalc - 1);
                  }
                }}
                disabled={currentPage >= totalPagesCalc - 1}
                className="p-1.5 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Dernière page"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Overlay pour fermer le menu colonnes */}
      {showColumnMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowColumnMenu(false)}
        />
      )}
    </div>
  );
}
